import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { BBox } from "./core/bitmap";
import { useT } from "./i18n";

interface Rect { x0: number; y0: number; x1: number; y1: number }

const normalize = (r: Rect): Rect => ({
  x0: Math.min(r.x0, r.x1), y0: Math.min(r.y0, r.y1),
  x1: Math.max(r.x0, r.x1), y1: Math.max(r.y0, r.y1),
});

/**
 * Lets you drag rectangles over the image to erase parts of the design -- drop a
 * name and keep only the title, for instance.
 *
 * It exists because removing a word does NOT justify regenerating the image: if
 * the area is white around it, painting it white is exact and free.
 */
export default function EraseOverlay({
  width,
  height,
  rects,
  onChange,
}: {
  width: number;
  height: number;
  rects: BBox[];
  onChange: (rects: BBox[]) => void;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<Rect | null>(null);

  /** Maps pointer coordinates to pixels of the original image. */
  const toImage = useCallback(
    (e: ReactPointerEvent) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return { x: 0, y: 0 };
      return {
        x: Math.round(((e.clientX - box.left) / box.width) * width),
        y: Math.round(((e.clientY - box.top) / box.height) * height),
      };
    },
    [width, height],
  );

  const down = (e: ReactPointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = toImage(e);
    setDragging({ x0: x, y0: y, x1: x, y1: y });
  };

  const move = (e: ReactPointerEvent) => {
    if (!dragging) return;
    const { x, y } = toImage(e);
    setDragging((d) => (d ? { ...d, x1: x, y1: y } : null));
  };

  const up = () => {
    if (!dragging) return;
    const r = normalize(dragging);
    setDragging(null);
    // A click without a drag erases nothing: degenerate rectangles are discarded.
    if (r.x1 - r.x0 < 4 || r.y1 - r.y0 < 4) return;
    onChange([...rects, r]);
  };

  const pct = (r: Rect) => ({
    left: `${(r.x0 / width) * 100}%`,
    top: `${(r.y0 / height) * 100}%`,
    width: `${((r.x1 - r.x0) / width) * 100}%`,
    height: `${((r.y1 - r.y0) / height) * 100}%`,
  });

  return (
    <div
      ref={ref}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => setDragging(null)}
      className="absolute inset-0 cursor-crosshair touch-none"
      role="application"
      aria-label={t("erase.aria")}
    >
      {rects.map((r, i) => (
        <button
          key={`${r.x0}-${r.y0}-${i}`}
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onChange(rects.filter((_, j) => j !== i))}
          style={pct(r)}
          title="Quitar este borrado"
          aria-label={`Quitar el borrado ${i + 1}`}
          className="absolute border-2 border-dashed border-[var(--color-bad)] bg-[var(--color-bad)]/15"
        />
      ))}
      {dragging && (
        <div
          style={pct(normalize(dragging))}
          className="pointer-events-none absolute border-2 border-dashed border-[var(--color-bad)] bg-[var(--color-bad)]/25"
        />
      )}
    </div>
  );
}
